import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, AlertCircle, Loader2, BellRing, CheckCircle, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

export const PortalGuruPengaturanAlert: React.FC = () => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Custom modal state
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);


    // State for thresholds
    const [sakitDays, setSakitDays] = useState<number>(3);
    const [alphaKelasMapel, setAlphaKelasMapel] = useState<number>(5);
    const [alphaTahfidzWaktu, setAlphaTahfidzWaktu] = useState<number>(4);
    const [batasTindakHari, setBatasTindakHari] = useState<number>(7);
    const [batasTeruskan, setBatasTeruskan] = useState<number>(3);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('alert_settings')
                .select('*')
                .eq('id', 1)
                .single();

            if (error && error.code !== 'PGRST116') {
                console.error('Error fetching alert settings:', error);
            } else if (data) {
                setSakitDays(data.sakit_days || 3);
                setAlphaKelasMapel(data.alpha_kelas_mapel || 5);
                setAlphaTahfidzWaktu(data.alpha_tahfidz_waktu || 4);
                setBatasTindakHari(data.batas_tindak_hari || 7);
                setBatasTeruskan(data.batas_teruskan || 3);
            }
        } catch (error) {
            console.error('Unexpected error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('alert_settings')
                .upsert({
                    id: 1, // Single row configuration
                    sakit_days: sakitDays,
                    alpha_kelas_mapel: alphaKelasMapel,
                    alpha_tahfidz_waktu: alphaTahfidzWaktu,
                    batas_tindak_hari: batasTindakHari,
                    batas_teruskan: batasTeruskan,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'id' });

            if (error) {
                console.error('Error saving alert settings:', error);
                setErrorMessage('Gagal menyimpan pengaturan.');
            } else {
                setSuccessMessage('Pengaturan berhasil disimpan!');
            }
        } catch (error) {
            console.error('Unexpected error during save:', error);
            setErrorMessage('Terjadi kesalahan yang tidak terduga.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-20 font-sans">
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-30 shadow-sm">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/portal-guru/admin/pengaturan')}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-lg font-bold text-gray-900 leading-tight">Pengaturan Alert Indisipliner</h1>
                            <p className="text-xs text-gray-500 font-medium">Batas ambang (Threshold)</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-4 max-w-3xl mx-auto space-y-6 mt-4">
                {/* Info Card */}
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <h3 className="text-sm font-bold text-blue-900 mb-1">Fungsi Pengaturan</h3>
                        <p className="text-xs text-blue-800 leading-relaxed">
                            Nilai yang Anda masukkan di sini akan menjadi penentu kapan sebuah alert peringatan muncul di halaman dashboard (Wali Kelas, Murobbi, Kepala Divisi, Guru Tahfidz).
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-brand-primary animate-spin mb-4" />
                        <p className="text-sm text-gray-500">Memuat pengaturan...</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-6">

                        {/* Sakit */}
                        <div>
                            <label className="block text-sm font-bold text-gray-900 mb-2">Batas Hari Santri Sakit Berturut-turut</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="number"
                                    min="1"
                                    value={sakitDays}
                                    onChange={(e) => setSakitDays(parseInt(e.target.value) || 1)}
                                    className="w-24 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all text-gray-900 font-medium"
                                />
                                <span className="text-sm text-gray-600">Hari</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Tidak lagi menjadi kasus indisipliner. Santri yang sakit selama ini atau lebih tampil di tab "Sakit ≥ N hari" pada Dasbor Klinik → Daftar Sakit Aktif.</p>
                        </div>

                        <hr className="border-gray-100" />

                        {/* Kelas */}
                        <div>
                            <label className="block text-sm font-bold text-gray-900 mb-2">Batas Alpha Presensi Kelas Berturut-turut</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="number"
                                    min="1"
                                    value={alphaKelasMapel}
                                    onChange={(e) => setAlphaKelasMapel(parseInt(e.target.value) || 1)}
                                    className="w-24 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all text-gray-900 font-medium"
                                />
                                <span className="text-sm text-gray-600">Mata Pelajaran (Pertemuan)</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Menghitung jumlah ketidakhadiran (Alpha) pada jam pelajaran akademik berturut-turut.</p>
                        </div>

                        <hr className="border-gray-100" />

                        {/* Tahfidz */}
                        <div>
                            <label className="block text-sm font-bold text-gray-900 mb-2">Batas Alpha Presensi Tahfidz Berturut-turut</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="number"
                                    min="1"
                                    value={alphaTahfidzWaktu}
                                    onChange={(e) => setAlphaTahfidzWaktu(parseInt(e.target.value) || 1)}
                                    className="w-24 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all text-gray-900 font-medium"
                                />
                                <span className="text-sm text-gray-600">Waktu Halaqoh</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Setiap hari biasanya ada 2 waktu tahfidz. Jika diset 4, berarti alert muncul setelah 2 hari berturut-turut alpha penuh.</p>
                        </div>

                        <hr className="border-gray-100" />

                        {/* Kasus Indisipliner (docs/PANDUAN_KASUS_INDISIPLINER.md) */}
                        <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 text-xs text-slate-600 leading-relaxed">
                            Rekap pekanan berjalan otomatis setiap <b>Senin pukul 01.00 WIB</b> (alpha Senin sampai Sabtu pekan lalu), dan alpha beruntun dicek setiap hari pukul 19.00 WIB. Hasilnya disimpan sebagai kasus dan masuk ke wali kelas santri.
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-900 mb-2">Batas Waktu Tindak Lanjut</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="number"
                                    min="1"
                                    value={batasTindakHari}
                                    onChange={(e) => setBatasTindakHari(parseInt(e.target.value) || 1)}
                                    className="w-24 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all text-gray-900 font-medium"
                                />
                                <span className="text-sm text-gray-600">Hari</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Kasus yang belum ditindak lebih dari batas ini ditandai merah "Terlambat".</p>
                        </div>

                        <hr className="border-gray-100" />

                        <div>
                            <label className="block text-sm font-bold text-gray-900 mb-2">Minimal Tindakan Sebelum Diteruskan</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="number"
                                    min="1"
                                    value={batasTeruskan}
                                    onChange={(e) => setBatasTeruskan(parseInt(e.target.value) || 1)}
                                    className="w-24 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all text-gray-900 font-medium"
                                />
                                <span className="text-sm text-gray-600">Kasus per semester</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Wali kelas baru bisa meneruskan kasus ke Waka Kesiswaan atau Kepala Divisi bila santri itu sudah sekian kali ditindak di semester berjalan. Hitungan mulai dari nol setiap semester.</p>
                        </div>

                        {/* Save Action */}
                        <div className="pt-4 flex justify-end">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 bg-brand-primary text-white px-6 py-2.5 rounded-lg font-bold hover:bg-brand-primary-dark transition-colors disabled:opacity-50"
                            >
                                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                                <span>{saving ? 'Menyimpan...' : 'Simpan Pengaturan'}</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>


            {/* Custom Success Modal */}
            {successMessage && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center shadow-xl animate-in zoom-in-95">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Berhasil!</h3>
                        <p className="text-gray-600 mb-6">{successMessage}</p>
                        <button
                            onClick={() => setSuccessMessage(null)}
                            className="w-full bg-brand-primary text-white font-bold py-3 rounded-xl hover:bg-brand-primary-dark transition-colors"
                        >
                            Tutup
                        </button>
                    </div>
                </div>
            )}

            {/* Custom Error Modal */}
            {errorMessage && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center shadow-xl animate-in zoom-in-95">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertTriangle className="w-8 h-8 text-red-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Terjadi Kesalahan</h3>
                        <p className="text-gray-600 mb-6">{errorMessage}</p>
                        <button
                            onClick={() => setErrorMessage(null)}
                            className="w-full bg-red-600 text-white font-bold py-3 rounded-xl hover:bg-red-700 transition-colors"
                        >
                            Tutup
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
